/**
 * Proxies d'interception Firebase (web, SDK compat) pour le mode démo.
 *
 * Spécifique web — le mobile (SDK modulaire @react-native-firebase) a son
 * propre point d'interception dans firestoreErrorHelper.
 *
 * Trois wrappers :
 * - wrapFirestore(db)   : anonymise doc.data() sur get()/onSnapshot(), bloque
 *                         les écritures (set/update/delete/add/batch/transaction)
 * - wrapRealTimeDb(db)  : idem pour la Realtime Database (quizz/obs streaming)
 * - wrapFunctions(fns)  : anonymise les réponses des Cloud Functions callable
 *
 * Le flag est lu à CHAQUE livraison de snapshot (isDemoActive()), donc le
 * toggle agit immédiatement sans re-création des instances. Hors mode démo,
 * les snapshots sont retournés tels quels (surcoût quasi nul).
 */

import { isDemoActive } from "./demoState";
import { anonymizeDoc, substitute } from "./engine";
import { createDemoWriteBlockedError } from "./demoWriteBlock";
import { createStoragePathWithoutParkError, storagePathLacksPark } from "../utils/storage/storagePathGuard";

const WRAPPED = Symbol("akongo.demoWrapped");
const UNWRAP = Symbol("akongo.demoUnwrap");

/** Retourne le snapshot natif sous-jacent si l'argument est un proxy démo. */
export function unwrapDemoSnapshot(value) {
  return value && value[UNWRAP] ? value[UNWRAP] : value;
}

// Lecture seule stricte en mode démo (décision utilisateur 2026-07-05).
const BLOCK_WRITES_IN_DEMO = true;

function rejectWrite(method) {
  const message = `Mode démo actif : écriture Firestore bloquée (${method}). / Demo mode: writes are disabled.`;
  console.warn(message);
  return Promise.reject(createDemoWriteBlockedError(message));
}

function shouldBlockWrite() {
  return BLOCK_WRITES_IN_DEMO && isDemoActive();
}

// --- Snapshots ---------------------------------------------------------------

function anonymizeFieldPathValue(fieldPath, value) {
  if (typeof value !== "string" || !value.trim()) return value;
  const segments = String(fieldPath).split(".");
  const lastSegment = segments[segments.length - 1];
  // Réutilise le registre du moteur via un mini-doc
  const probe = anonymizeDoc({ [lastSegment]: value });
  return probe[lastSegment];
}

function wrapDocSnapshot(snap) {
  if (!snap || snap[WRAPPED]) return snap;
  // Démo OFF : PASSTHROUGH total (snapshot natif, aucun Proxy) — garantie
  // zéro régression hors mode démo (curseurs startAfter, instanceof, ===).
  if (!isDemoActive()) return snap;
  return new Proxy(snap, {
    get(target, prop) {
      if (prop === WRAPPED) return true;
      if (prop === UNWRAP) return target;
      if (prop === "data") {
        return (...args) => {
          const data = target.data(...args);
          return isDemoActive() ? anonymizeDoc(data) : data;
        };
      }
      if (prop === "get") {
        return (fieldPath, ...rest) => {
          const value = target.get(fieldPath, ...rest);
          return isDemoActive() ? anonymizeFieldPathValue(fieldPath, value) : value;
        };
      }
      if (prop === "ref") {
        return wrapQueryLike(target.ref);
      }
      const value = target[prop];
      return typeof value === "function" ? value.bind(target) : value;
    },
  });
}

function wrapQuerySnapshot(snap) {
  if (!snap || snap[WRAPPED]) return snap;
  if (!isDemoActive()) return snap;
  return new Proxy(snap, {
    get(target, prop) {
      if (prop === WRAPPED) return true;
      if (prop === UNWRAP) return target;
      if (prop === "docs") {
        return target.docs.map(wrapDocSnapshot);
      }
      if (prop === "forEach") {
        return (callback, thisArg) =>
          target.forEach((doc, index) => callback.call(thisArg, wrapDocSnapshot(doc), index));
      }
      if (prop === "docChanges") {
        return (...args) =>
          target.docChanges(...args).map((change) => ({
            type: change.type,
            doc: wrapDocSnapshot(change.doc),
            oldIndex: change.oldIndex,
            newIndex: change.newIndex,
          }));
      }
      const value = target[prop];
      if (typeof value === "function") return value.bind(target);
      // .query (Query) : wrap pour couvrir une relance de lecture
      return isQueryLike(value) ? wrapQueryLike(value) : value;
    },
  });
}

export function wrapSnapshot(snap) {
  if (!snap || snap[WRAPPED]) return snap;
  if (Array.isArray(snap.docs)) return wrapQuerySnapshot(snap);
  if (typeof snap.data === "function") return wrapDocSnapshot(snap);
  return snap;
}

// onSnapshot(observer | onNext[, onError][, options…]) — on enveloppe le
// premier callback (onNext) ou observer.next pour livrer un snapshot anonymisé.
function mapOnSnapshotArgs(args) {
  let onNextWrapped = false;
  return args.map((arg) => {
    if (!onNextWrapped && typeof arg === "function") {
      onNextWrapped = true;
      return (snap, ...rest) => arg(wrapSnapshot(snap), ...rest);
    }
    if (!onNextWrapped && arg && typeof arg === "object" && typeof arg.next === "function") {
      onNextWrapped = true;
      return { ...arg, next: (snap) => arg.next(wrapSnapshot(snap)) };
    }
    return arg;
  });
}

// --- Références / requêtes chaînables -----------------------------------------

const REF_WRITE_METHODS = new Set(["set", "update", "delete", "add"]);

// Méthodes de curseur : en mode démo, elles peuvent recevoir un snapshot
// proxifié — le SDK doit recevoir le snapshot natif.
const CURSOR_METHODS = new Set(["startAfter", "startAt", "endAt", "endBefore"]);

function isQueryLike(value) {
  return (
    value &&
    typeof value === "object" &&
    typeof value.onSnapshot === "function" &&
    (typeof value.get === "function" || typeof value.doc === "function")
  );
}

function wrapQueryLike(target) {
  if (!target || target[WRAPPED]) return target;
  return new Proxy(target, {
    get(t, prop) {
      if (prop === WRAPPED) return true;
      if (prop === UNWRAP) return t;
      const value = t[prop];
      if (prop === "get") {
        return (...args) => t.get(...args).then(wrapSnapshot);
      }
      if (prop === "onSnapshot") {
        return (...args) => t.onSnapshot(...mapOnSnapshotArgs(args));
      }
      if (REF_WRITE_METHODS.has(prop) && typeof value === "function") {
        return (...args) => {
          if (shouldBlockWrite()) return rejectWrite(prop);
          return maybeWrap(value.apply(t, args));
        };
      }
      if (CURSOR_METHODS.has(prop) && typeof value === "function") {
        return (...args) => maybeWrap(value.apply(t, args.map(unwrapDemoSnapshot)));
      }
      if (typeof value === "function") {
        return (...args) => maybeWrap(value.apply(t, args));
      }
      // Propriétés chaînables (.parent…) : wrap pour ne pas offrir de
      // chemin de lecture non anonymisé
      return isQueryLike(value) ? wrapQueryLike(value) : value;
    },
  });
}

function maybeWrap(result) {
  if (result && typeof result.then === "function") {
    // add() → Promise<DocumentReference>
    return result.then((resolved) => (isQueryLike(resolved) ? wrapQueryLike(resolved) : resolved));
  }
  return isQueryLike(result) ? wrapQueryLike(result) : result;
}

function wrapWriteBatch(batch) {
  if (!batch || batch[WRAPPED]) return batch;
  return new Proxy(batch, {
    get(t, prop) {
      if (prop === WRAPPED) return true;
      if (prop === "commit") {
        return (...args) => {
          if (shouldBlockWrite()) return rejectWrite("batch.commit");
          return t.commit(...args);
        };
      }
      const value = t[prop];
      if (typeof value === "function") {
        // set/update/delete d'un batch retournent le batch → re-wrap
        return (...args) => {
          const res = value.apply(t, args);
          return res === t ? wrapWriteBatch(res) : res;
        };
      }
      return value;
    },
  });
}

/** Enveloppe une instance Firestore compat. Idempotent (double-wrap impossible). */
export function wrapFirestore(db) {
  if (!db || db[WRAPPED]) return db;
  return new Proxy(db, {
    get(target, prop) {
      if (prop === WRAPPED) return true;
      const value = target[prop];
      if (prop === "collection" || prop === "collectionGroup" || prop === "doc") {
        return (...args) => wrapQueryLike(value.apply(target, args));
      }
      if (prop === "batch") {
        return (...args) => wrapWriteBatch(value.apply(target, args));
      }
      if (prop === "runTransaction") {
        return (...args) => {
          if (shouldBlockWrite()) return rejectWrite("runTransaction");
          return value.apply(target, args);
        };
      }
      return typeof value === "function" ? value.bind(target) : value;
    },
  });
}

// --- Realtime Database ---------------------------------------------------------

const RTDB_WRITE_METHODS = new Set(["set", "update", "remove", "push", "setWithPriority", "transaction"]);

function wrapRtdbSnapshot(snap) {
  if (!snap || snap[WRAPPED] || !isDemoActive()) return snap;
  return new Proxy(snap, {
    get(target, prop) {
      if (prop === WRAPPED) return true;
      if (prop === "val") {
        return () => anonymizeDoc(target.val());
      }
      if (prop === "forEach") {
        return (callback) => target.forEach((child) => callback(wrapRtdbSnapshot(child)));
      }
      if (prop === "child") {
        return (...args) => wrapRtdbSnapshot(target.child(...args));
      }
      const value = target[prop];
      return typeof value === "function" ? value.bind(target) : value;
    },
  });
}

function wrapRtdbRef(ref) {
  if (!ref || ref[WRAPPED]) return ref;
  return new Proxy(ref, {
    get(target, prop) {
      if (prop === WRAPPED) return true;
      const value = target[prop];
      if (prop === "once") {
        return (...args) => target.once(...args).then(wrapRtdbSnapshot);
      }
      if (prop === "on") {
        return (eventType, callback, ...rest) =>
          target.on(eventType, (snap, prevKey) => callback(wrapRtdbSnapshot(snap), prevKey), ...rest);
      }
      if (prop === "child" || prop === "orderByChild" || prop === "orderByKey" ||
          prop === "orderByValue" || prop === "limitToFirst" || prop === "limitToLast" ||
          prop === "startAt" || prop === "endAt" || prop === "equalTo") {
        return (...args) => wrapRtdbRef(value.apply(target, args));
      }
      if (RTDB_WRITE_METHODS.has(prop) && typeof value === "function") {
        return (...args) => {
          if (shouldBlockWrite()) return rejectWrite(`rtdb.${prop}`);
          return value.apply(target, args);
        };
      }
      return typeof value === "function" ? value.bind(target) : value;
    },
  });
}

/** Enveloppe une instance Realtime Database compat. Idempotent. */
export function wrapRealTimeDb(database) {
  if (!database || database[WRAPPED]) return database;
  return new Proxy(database, {
    get(target, prop) {
      if (prop === WRAPPED) return true;
      const value = target[prop];
      if (prop === "ref") {
        return (...args) => wrapRtdbRef(value.apply(target, args));
      }
      return typeof value === "function" ? value.bind(target) : value;
    },
  });
}

// --- Cloud Functions callable ----------------------------------------------------

// Lecture seule stricte : les callables MUTATEURS (écriture via l'admin SDK,
// donc invisibles pour les gardes Firestore) sont bloqués en démo. Détection
// par verbe — un callable read-only qui matche par erreur se corrige en
// l'ajoutant à DEMO_SAFE_CALLABLES.
const MUTATOR_CALLABLE_RE =
  /^(create|update|edit|delete|remove|store|add|set|merge|import|execute|submit|handle|renew|archive|restore|soft|bulk|migrate|generate|send|assign|toggle|apply|sync|upload|write)/i;
const DEMO_SAFE_CALLABLES = new Set([]);

export function isCallableBlockedInDemo(name) {
  return (
    shouldBlockWrite() &&
    typeof name === "string" &&
    MUTATOR_CALLABLE_RE.test(name) &&
    !DEMO_SAFE_CALLABLES.has(name)
  );
}

/** Enveloppe une instance Functions compat : anonymise result.data des
 * callables, et bloque les callables mutateurs en démo. Idempotent. */
export function wrapFunctions(functionsInstance) {
  if (!functionsInstance || functionsInstance[WRAPPED]) return functionsInstance;
  return new Proxy(functionsInstance, {
    get(target, prop) {
      if (prop === WRAPPED) return true;
      const value = target[prop];
      if (prop === "httpsCallable") {
        return (...callableArgs) => {
          const callable = value.apply(target, callableArgs);
          const callableName = callableArgs[0];
          return (data) => {
            if (isCallableBlockedInDemo(callableName)) {
              return rejectWrite(`httpsCallable(${callableName})`);
            }
            return callable(data).then((result) =>
              isDemoActive() ? { ...result, data: anonymizeDoc(result.data) } : result
            );
          };
        };
      }
      return typeof value === "function" ? value.bind(target) : value;
    },
  });
}

// --- Storage (uploads) -------------------------------------------------------------

const STORAGE_REF_WRITE_METHODS = new Set(["put", "putString", "delete", "updateMetadata"]);
const STORAGE_REF_UPLOAD_METHODS = new Set(["put", "putString"]);

function wrapStorageRef(ref) {
  if (!ref || ref[WRAPPED]) return ref;
  return new Proxy(ref, {
    get(target, prop) {
      if (prop === WRAPPED) return true;
      const value = target[prop];
      if (STORAGE_REF_WRITE_METHODS.has(prop) && typeof value === "function") {
        return (...args) => {
          if (shouldBlockWrite()) return rejectWrite(`storage.${prop}`);
          // Jamais d'envoi sous `undefined/` : le parc doit être connu (storagePathGuard).
          if (STORAGE_REF_UPLOAD_METHODS.has(prop) && storagePathLacksPark(target.fullPath)) {
            throw createStoragePathWithoutParkError(target.fullPath);
          }
          return value.apply(target, args);
        };
      }
      if ((prop === "child" || prop === "parent" || prop === "root") && typeof value === "function") {
        return (...args) => wrapStorageRef(value.apply(target, args));
      }
      if (prop === "child" || prop === "parent" || prop === "root") {
        return wrapStorageRef(value);
      }
      return typeof value === "function" ? value.bind(target) : value;
    },
  });
}

/** Enveloppe une instance Storage compat : bloque les uploads/suppressions en démo. */
export function wrapStorage(storageInstance) {
  if (!storageInstance || storageInstance[WRAPPED]) return storageInstance;
  return new Proxy(storageInstance, {
    get(target, prop) {
      if (prop === WRAPPED) return true;
      const value = target[prop];
      if ((prop === "ref" || prop === "refFromURL") && typeof value === "function") {
        return (...args) => wrapStorageRef(value.apply(target, args));
      }
      return typeof value === "function" ? value.bind(target) : value;
    },
  });
}

export { substitute };
